/**
 * Procedural impulse responses.
 *
 * An IR here is: a pre-delay, a set of discrete early reflections whose pattern
 * encodes the geometry of the space (flutter for an alley, sparse slapback for an
 * open square, dense scatter for a room), and an exponentially decaying, gradually
 * darkening diffuse tail whose slope is set from the target RT60.
 *
 * The tail is what tells the player which room they are standing in — a gunshot's
 * decay changing as you step out of an alley into a square is the single biggest
 * environmental realism cue in the mix.
 */
import { makeRng } from '../../core/Rand.js';
import { noiseSlice } from './noise.js';
import { onePoleLP, highpass, sweepLP, fadeOut, TAU } from './dsp.js';

/**
 * @typedef {Object} IRSpec
 * @property {number} rt60          target reverberation time, seconds
 * @property {number} preDelay      seconds before the first reflection
 * @property {number} erCount       number of discrete early reflections
 * @property {number} erSpread      seconds over which early reflections arrive
 * @property {number} erGain        early reflection level
 * @property {number} tailGain      diffuse tail level
 * @property {number} flutter       0 = scattered taps, 1 = regular comb (alley)
 * @property {number} hfDamp        0..1, how much the tail darkens over its length
 * @property {number} lowCut        Hz, high-pass on the whole IR
 * @property {number} width         0..1 stereo decorrelation
 */

export const ZONE_IR = {
  outdoor: {
    rt60: 0.85, preDelay: 0.020, erCount: 9, erSpread: 0.26, erGain: 0.30,
    tailGain: 0.30, flutter: 0.0, hfDamp: 0.75, lowCut: 95, width: 1.0,
    desc: 'open square — sparse slapback off distant façades, dark diffuse wash',
  },
  alley: {
    rt60: 1.45, preDelay: 0.005, erCount: 26, erSpread: 0.12, erGain: 0.62,
    tailGain: 0.48, flutter: 0.85, hfDamp: 0.5, lowCut: 80, width: 0.55,
    desc: 'narrow alley — hard parallel walls, pronounced flutter echo',
  },
  small_interior: {
    rt60: 0.42, preDelay: 0.0035, erCount: 30, erSpread: 0.032, erGain: 0.58,
    tailGain: 0.52, flutter: 0.15, hfDamp: 0.62, lowCut: 70, width: 0.7,
    desc: 'small room — very dense early field, short controlled tail',
  },
  large_interior: {
    rt60: 2.30, preDelay: 0.013, erCount: 34, erSpread: 0.095, erGain: 0.42,
    tailGain: 0.60, flutter: 0.25, hfDamp: 0.55, lowCut: 60, width: 0.85,
    desc: 'warehouse / hall — long dense tail, audible build-up',
  },
};

/** Amplitude envelope that yields the requested RT60 (-60 dB over rt60 seconds). */
function decayGain(t, rt60) {
  return Math.exp((-6.907755 * t) / Math.max(0.02, rt60));
}

/**
 * Build a stereo impulse response as raw Float32Arrays.
 * @param {IRSpec} spec
 * @param {number} sampleRate
 * @param {number} seed
 * @returns {{ l: Float32Array, r: Float32Array, seconds: number }}
 */
export function buildIRData(spec, sampleRate, seed = 0x1234) {
  const s = { ...ZONE_IR.small_interior, ...spec };
  const seconds = Math.min(3.2, s.rt60 * 1.3 + s.preDelay + s.erSpread + 0.05);
  const n = Math.floor(seconds * sampleRate);
  const rng = makeRng(seed >>> 0);

  const chans = [];
  for (let c = 0; c < 2; c++) {
    const out = new Float32Array(n);

    /* ---- diffuse tail: decaying noise that darkens as it decays ---- */
    const tail = noiseSlice('white', n, rng);
    for (let i = 0; i < n; i++) {
      const t = i / sampleRate;
      // build-up: real rooms take a few ms to become diffuse
      const build = Math.min(1, t / Math.max(0.004, s.preDelay + s.erSpread * 0.5));
      tail[i] *= decayGain(t, s.rt60) * build * s.tailGain;
    }
    // air absorption / wall damping: cutoff walks down over the tail
    const fHi = 11000;
    const fLo = 11000 * (1 - 0.92 * s.hfDamp) + 700;
    sweepLP(tail, sampleRate, (t) => {
      const k = Math.min(1, t / Math.max(0.05, s.rt60));
      return fHi * Math.pow(fLo / fHi, k);
    });

    /* ---- early reflections ---- */
    const er = new Float32Array(n);
    const combStep = s.erSpread / Math.max(1, s.erCount);
    for (let k = 0; k < s.erCount; k++) {
      const u = (k + 1) / s.erCount;
      const scattered = s.preDelay + Math.pow(rng(), 0.7) * s.erSpread;
      const comb = s.preDelay + combStep * (k + 1) + (rng() - 0.5) * combStep * 0.18;
      const t = scattered * (1 - s.flutter) + comb * s.flutter;
      const idx = Math.floor(t * sampleRate);
      if (idx <= 0 || idx >= n - 8) continue;
      const g = s.erGain * decayGain(t, s.rt60 * 0.85) * (0.45 + 0.55 * rng()) * (1 - 0.5 * u);
      const pol = rng() < 0.5 ? -1 : 1;
      // each reflection is a short smeared burst, not a bare dirac — a wall is not a mirror
      const w = 3 + Math.floor(rng() * 12);
      for (let i = 0; i < w && idx + i < n; i++) {
        er[idx + i] += pol * g * (rng() * 2 - 1) * (1 - i / w);
      }
      er[idx] += pol * g * 0.9;
    }
    onePoleLP(er, sampleRate, 5200 + rng() * 3500);

    for (let i = 0; i < n; i++) out[i] = tail[i] + er[i];
    highpass(out, sampleRate, s.lowCut, 0.7);
    fadeOut(out, Math.floor(sampleRate * 0.02));
    chans.push(out);
  }

  // stereo width: blend the two decorrelated channels back toward mono as width drops
  const [l, r] = chans;
  const w = Math.max(0, Math.min(1, s.width));
  for (let i = 0; i < n; i++) {
    const m = (l[i] + r[i]) * 0.5;
    l[i] = m + (l[i] - m) * w;
    r[i] = m + (r[i] - m) * w;
  }
  return { l, r, seconds };
}

/** Build the IR directly as an AudioBuffer belonging to `ac`. */
export function buildIRBuffer(ac, spec, seed = 0x1234) {
  const { l, r } = buildIRData(spec, ac.sampleRate, seed);
  const buf = ac.createBuffer(2, l.length, ac.sampleRate);
  buf.copyToChannel(l, 0);
  buf.copyToChannel(r, 1);
  return buf;
}

/**
 * A long, very diffuse, heavily damped tail used for gunfire far from the listener
 * — the "rolling thunder" you hear a second after a distant contact.
 */
export function distantTailData(sampleRate, seed = 0x77, rt60 = 1.9) {
  return buildIRData({
    rt60, preDelay: 0.03, erCount: 6, erSpread: 0.42, erGain: 0.22,
    tailGain: 0.55, flutter: 0, hfDamp: 0.95, lowCut: 45, width: 1,
  }, sampleRate, seed);
}

/** Convenience: a ConvolverNode already loaded with a zone IR. */
export function zoneConvolver(ac, zoneName, seed = 0x1234) {
  const conv = ac.createConvolver();
  conv.normalize = true;
  conv.buffer = buildIRBuffer(ac, ZONE_IR[zoneName] || ZONE_IR.small_interior, seed);
  return conv;
}

export const _internal = { decayGain, TAU };
